/*
 * Kate Hill Flowers - Ready wedding flowers builder
 * Built 2026-08-10. Delivery calculator 2026-08-11. Box model 2026-08-11.
 * Accessories-only rule 2026-08-12 (Kate approved via Al).
 * Look no longer auto-adds a bouquet + state survives navigation 2026-08-16 (Al).
 * Sundays blocked 2026-08-18 (Al) - the studio is closed, every day of the year.
 * Pairs with sections/kh-wedding-builder.liquid.
 *
 * Date property is "Another date" - the key the existing product flow writes,
 * so the studio sees one date field everywhere. cart-flow.liquid detects a
 * wedding order by the PAIR of "Another date" + "Delivery or pick up".
 *
 * DELIVERY MODEL (Kate + Al, 11-12 Aug 2026), and it must stay identical to
 * the Postcode Shipping config or the shown price stops equalling the charged
 * one:
 *   wedding rate for the suburb = 2x its Standard rate (the express rate)
 *   every BOUQUET (bridal or bridesmaid) travels in its own box, so each one
 *     attracts the rate
 *   ALL buttonholes and corsages travel together in a single box, so they
 *     attract the rate exactly once, however many there are
 *   delivery = wedding rate x (bouquet count + 1 if any accessories)
 *   ACCESSORIES-ONLY orders (no bouquet in the cart) are indistinguishable
 *     from formal/event orders at checkout, so they travel as a standard
 *     delivery instead: 1x the suburb's Standard rate, one box, NO delivery
 *     window. Kate blessed this on 12 Aug 2026.
 * App side: two product groups ("Ready Wedding Bouquets" Quantity rate,
 * "Ready Wedding Accessories" Flat rate, both titled Wedding Delivery,
 * summed by matching title) plus rate rules so accessories charge wedding
 * rates only when the cart also holds a bouquet.
 */
(function () {
  'use strict';

  var DATE_PROPERTY = 'Another date';
  var METHOD_PROPERTY = 'Delivery or pick up';
  var SUBURB_PROPERTY = 'Delivery suburb';
  var WINDOW_PROPERTY = 'Delivery window';

  // Roles that travel as their own box. Anything else rides in the shared
  // accessories box.
  function isBouquet(role) {
    return role === 'bridal' || role === 'bridesmaid';
  }

  // The studio is closed on Sundays - no delivery and no pickup, ever.
  // A native date input cannot grey out a weekday, so the date is validated
  // here instead and the summary stays locked until a working day is chosen.
  function isSunday(iso) {
    if (!iso) return false;
    var bits = iso.split('-').map(Number);
    return new Date(Date.UTC(bits[0], bits[1] - 1, bits[2])).getUTCDay() === 0;
  }

  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function todayInMelbourne() {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Melbourne',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  }

  function addDays(iso, days) {
    var bits = iso.split('-').map(Number);
    var d = new Date(Date.UTC(bits[0], bits[1] - 1, bits[2]));
    d.setUTCDate(d.getUTCDate() + days);
    return (
      d.getUTCFullYear() +
      '-' +
      String(d.getUTCMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getUTCDate()).padStart(2, '0')
    );
  }

  function prettyDate(iso) {
    var bits = iso.split('-').map(Number);
    var d = new Date(Date.UTC(bits[0], bits[1] - 1, bits[2]));
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(d);
  }

  function init(root) {
    // A second boot (bfcache restore, section reload) must re-run render, not
    // bail out: the browser restores the input values but not the summary the
    // script drew, so the two silently disagree. (Al, 16 Aug 2026)
    if (root.dataset.khWbReady === 'true') {
      if (typeof root.khWbRender === 'function') root.khWbRender();
      return;
    }
    root.dataset.khWbReady = 'true';

    var leadDays = parseInt(root.dataset.leadDays, 10);
    if (isNaN(leadDays)) leadDays = 2;

    var dateInput = root.querySelector('[data-kh-wb-date]');
    var dateMsg = root.querySelector('[data-kh-wb-date-msg]');
    var lookInputs = root.querySelectorAll('[data-kh-wb-look]');
    var panels = root.querySelectorAll('[data-kh-wb-panel]');
    var panelEmpty = root.querySelector('[data-kh-wb-panel-empty]');
    var methodInputs = root.querySelectorAll('[data-kh-wb-method]');
    var deliveryPanel = root.querySelector('[data-kh-wb-delivery-panel]');
    var suburbInput = root.querySelector('[data-kh-wb-suburb]');
    var suburbList = root.querySelector('[data-kh-wb-suburb-list]');
    var suburbMsg = root.querySelector('[data-kh-wb-suburb-msg]');
    var windowsBox = root.querySelector('[data-kh-wb-windows]');
    var summaryList = root.querySelector('[data-kh-wb-summary-list]');
    var summaryEmpty = root.querySelector('[data-kh-wb-summary-empty]');
    var summaryDate = root.querySelector('[data-kh-wb-summary-date]');
    var subtotalEl = root.querySelector('[data-kh-wb-subtotal]');
    var deliveryRow = root.querySelector('[data-kh-wb-delivery-row]');
    var deliveryLabel = root.querySelector('[data-kh-wb-delivery-label]');
    var deliveryAmt = root.querySelector('[data-kh-wb-delivery-amt]');
    var totalEl = root.querySelector('[data-kh-wb-total]');
    var submitBtn = root.querySelector('[data-kh-wb-submit]');
    var hint = root.querySelector('[data-kh-wb-hint]');

    var minDate = addDays(todayInMelbourne(), leadDays);
    if (dateInput) dateInput.min = minDate;

    var zones = null;
    var zonesLoading = false;
    var suburb = null; // {name, postcode, rate} - rate is the STANDARD rate

    // ---- state persistence ------------------------------------------------
    // Everything used to live in the DOM only, so opening a product and coming
    // back lost the order. Session storage keeps it for the tab; it is cleared
    // once the items reach the real cart. (Al, 16 Aug 2026)
    var STORAGE_KEY = 'kh-wb:' + root.id;

    function saveState() {
      try {
        var qty = {};
        root.querySelectorAll('[data-kh-wb-item]').forEach(function (item) {
          var input = item.querySelector('[data-kh-wb-qty]');
          if (!input) return;
          var value = parseInt(input.value, 10);
          if (value > 0) qty[item.dataset.variant] = value;
        });
        var look = root.querySelector('[data-kh-wb-look]:checked');
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            date: dateInput ? dateInput.value : '',
            look: look ? look.value : '',
            method: chosenMethod(),
            deliveryWindow: chosenWindow(),
            suburb: suburb,
            qty: qty
          })
        );
      } catch (e) {
        /* private mode or quota - never block the builder */
      }
    }

    function clearState() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* nothing to do */
      }
    }

    function restoreState() {
      var saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (e) {
        return;
      }
      if (!saved) return;

      if (saved.date && dateInput) dateInput.value = saved.date;

      if (saved.look) {
        var lookInput = root.querySelector('[data-kh-wb-look][value="' + saved.look + '"]');
        if (lookInput) {
          lookInput.checked = true;
          panels.forEach(function (panel) {
            panel.hidden = panel.dataset.khWbPanel !== saved.look;
          });
          if (panelEmpty) panelEmpty.hidden = true;
        }
      }

      if (saved.method) {
        methodInputs.forEach(function (input) {
          input.checked = input.value === saved.method;
        });
      }

      if (saved.deliveryWindow) {
        root.querySelectorAll('[data-kh-wb-window]').forEach(function (input) {
          input.checked = input.value === saved.deliveryWindow;
        });
      }

      if (saved.suburb && saved.suburb.name) {
        suburb = saved.suburb;
        if (suburbInput) suburbInput.value = saved.suburb.name + ' ' + saved.suburb.postcode;
      }

      if (saved.qty) {
        Object.keys(saved.qty).forEach(function (variant) {
          root.querySelectorAll('[data-kh-wb-item][data-variant="' + variant + '"]').forEach(function (item) {
            var input = item.querySelector('[data-kh-wb-qty]');
            if (!input) return;
            input.value = saved.qty[variant];
            item.classList.add('kh-wb__item--active');
          });
        });
      }
    }

    function loadZones() {
      if (zones || zonesLoading || !root.dataset.zonesUrl) return;
      zonesLoading = true;
      fetch(root.dataset.zonesUrl)
        .then(function (r) { return r.json(); })
        .then(function (data) { zones = data; })
        .catch(function () { zonesLoading = false; });
    }

    function suburbHasRate() {
      return !!(suburb && suburb.rate !== null && suburb.rate !== undefined);
    }

    function weddingRate() {
      if (!suburbHasRate()) return null;
      return suburb.rate * 2 * 100; // cents
    }

    // Accessories-only orders travel as a normal delivery, so they pay the
    // suburb's Standard rate once, not the doubled wedding rate.
    function standardRate() {
      if (!suburbHasRate()) return null;
      return suburb.rate * 100; // cents
    }

    function chosenWindow() {
      var checked = root.querySelector('[data-kh-wb-window]:checked');
      return checked ? checked.value : '9am to 12pm';
    }

    function renderSuburbOptions(query) {
      suburbList.innerHTML = '';
      if (!zones || !query || query.length < 2) {
        suburbList.hidden = true;
        return;
      }
      var q = query.toLowerCase();
      var starts = [];
      var contains = [];
      zones.suburbs.forEach(function (s) {
        var n = s.name.toLowerCase();
        if (n.indexOf(q) === 0) starts.push(s);
        else if (n.indexOf(q) > 0 || s.postcode.indexOf(q) === 0) contains.push(s);
      });
      var options = starts.concat(contains).slice(0, 8);
      if (!options.length) {
        suburbList.hidden = true;
        return;
      }
      options.forEach(function (s) {
        var li = document.createElement('li');
        li.textContent = s.name + ' ' + s.postcode;
        li.setAttribute('role', 'option');
        li.tabIndex = 0;
        li.addEventListener('click', function () { chooseSuburb(s); });
        li.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); chooseSuburb(s); }
        });
        suburbList.appendChild(li);
      });
      suburbList.hidden = false;
    }

    function chooseSuburb(s) {
      suburb = s;
      suburbInput.value = s.name + ' ' + s.postcode;
      suburbList.hidden = true;
      if (s.rate === null || s.rate === undefined) {
        suburbMsg.textContent = 'We cannot book wedding delivery to ' + s.name + ' online. Please call the studio on 03 9510 8800.';
        suburbMsg.className = 'kh-wb__suburb-msg kh-wb__suburb-msg--warn';
        windowsBox.hidden = true;
      }
      render();
    }

    function clearSuburb() {
      if (!suburb) return;
      suburb = null;
      suburbMsg.textContent = '';
      suburbMsg.className = 'kh-wb__suburb-msg';
      windowsBox.hidden = true;
      render();
    }

    if (suburbInput) {
      suburbInput.addEventListener('focus', loadZones);
      suburbInput.addEventListener('input', function () {
        clearSuburb();
        loadZones();
        renderSuburbOptions(suburbInput.value.trim());
      });
      document.addEventListener('click', function (e) {
        if (!suburbList.contains(e.target) && e.target !== suburbInput) suburbList.hidden = true;
      });
    }

    function selectedItems() {
      var out = [];
      var seen = {};
      root.querySelectorAll('[data-kh-wb-item]').forEach(function (item) {
        var qtyInput = item.querySelector('[data-kh-wb-qty]');
        if (!qtyInput) return;
        var qty = parseInt(qtyInput.value, 10);
        if (!qty || qty < 1) return;
        var id = item.dataset.variant;
        if (seen[id]) return;
        seen[id] = true;
        out.push({
          id: id,
          title: item.dataset.title,
          price: parseInt(item.dataset.price, 10) || 0,
          role: item.dataset.role,
          quantity: qty
        });
      });
      return out;
    }

    // Boxes, not items: one per bouquet, plus one shared box if there are any
    // buttonholes or corsages at all.
    function deliveryBoxes(items) {
      var bouquets = 0;
      var accessories = 0;
      items.forEach(function (item) {
        if (isBouquet(item.role)) bouquets += item.quantity;
        else accessories += item.quantity;
      });
      return {
        bouquets: bouquets,
        accessories: accessories,
        total: bouquets + (accessories > 0 ? 1 : 0)
      };
    }

    function isAccessoriesOnly(boxes) {
      return boxes.accessories > 0 && boxes.bouquets === 0;
    }

    function syncDuplicates(sourceItem, value) {
      var id = sourceItem.dataset.variant;
      root.querySelectorAll('[data-kh-wb-item][data-variant="' + id + '"]').forEach(function (twin) {
        if (twin === sourceItem) return;
        var input = twin.querySelector('[data-kh-wb-qty]');
        if (!input) return;
        input.value = value;
        twin.classList.toggle('kh-wb__item--active', value > 0);
      });
    }

    function chosenMethod() {
      var checked = root.querySelector('[data-kh-wb-method]:checked');
      return checked ? checked.value : 'Delivery';
    }

    function dateIsValid() {
      return !!(
        dateInput &&
        dateInput.value &&
        dateInput.value >= minDate &&
        !isSunday(dateInput.value)
      );
    }

    function render() {
      var items = selectedItems();
      var subtotal = 0;

      summaryList.innerHTML = '';
      items.forEach(function (item) {
        var lineTotal = item.price * item.quantity;
        subtotal += lineTotal;
        var li = document.createElement('li');
        var left = document.createElement('span');
        left.textContent = item.quantity + ' x ' + item.title;
        var right = document.createElement('span');
        right.textContent = money(lineTotal);
        li.appendChild(left);
        li.appendChild(right);
        summaryList.appendChild(li);
      });

      summaryEmpty.hidden = items.length > 0;
      subtotalEl.textContent = money(subtotal);

      var isDelivery = chosenMethod() === 'Delivery';
      if (deliveryPanel) deliveryPanel.hidden = !isDelivery;

      var boxes = deliveryBoxes(items);
      var accOnly = isAccessoriesOnly(boxes);
      var rate = accOnly ? standardRate() : weddingRate();
      var deliveryTotal = 0;

      // The suburb line and the window radios follow the cart's shape: a cart
      // with a bouquet is a windowed wedding run; accessories alone travel as
      // a standard delivery with no window.
      if (suburbHasRate()) {
        if (accOnly) {
          suburbMsg.textContent = 'Delivery to ' + suburb.name + ': ' + money(standardRate()) + '. Corsages and buttonholes on their own travel as one box, by standard delivery.';
          windowsBox.hidden = true;
        } else {
          suburbMsg.textContent = 'Wedding delivery to ' + suburb.name + ': ' + money(weddingRate()) + ' a box.';
          windowsBox.hidden = false;
        }
        suburbMsg.className = 'kh-wb__suburb-msg kh-wb__suburb-msg--ok';
      }

      if (isDelivery && suburb && rate !== null && boxes.total > 0) {
        deliveryTotal = rate * boxes.total;

        if (accOnly) {
          deliveryLabel.textContent = 'Delivery, one box, standard delivery';
        } else {
          var parts = [];
          if (boxes.bouquets > 0) {
            parts.push(boxes.bouquets + (boxes.bouquets === 1 ? ' bouquet' : ' bouquets'));
          }
          if (boxes.accessories > 0) {
            parts.push(boxes.bouquets > 0 ? 'one box of accessories' : 'one box');
          }
          deliveryLabel.textContent = 'Delivery, ' + parts.join(' plus ') + ', ' + boxes.total + ' x ' + money(rate);
        }
        deliveryAmt.textContent = money(deliveryTotal);
        deliveryRow.hidden = false;
      } else {
        deliveryRow.hidden = true;
      }

      totalEl.textContent = money(subtotal + deliveryTotal);

      if (dateIsValid()) {
        summaryDate.hidden = false;
        summaryDate.textContent = 'For ' + prettyDate(dateInput.value);
      } else {
        summaryDate.hidden = true;
        summaryDate.textContent = '';
      }

      var deliveryReady = !isDelivery || suburbHasRate();
      var ready = dateIsValid() && items.length > 0 && deliveryReady;
      submitBtn.disabled = !ready;
      hint.classList.remove('kh-wb__hint--error');

      if (dateInput && isSunday(dateInput.value)) {
        hint.textContent = 'The studio is closed on Sundays. Please choose another date.';
      } else if (!dateIsValid()) {
        hint.textContent = 'Choose your wedding date to continue.';
      } else if (items.length === 0) {
        hint.textContent = 'Add at least one piece to continue.';
      } else if (!deliveryReady) {
        hint.textContent = suburb
          ? 'Please call the studio to arrange delivery to ' + suburb.name + ', or choose pick up.'
          : 'Enter your delivery suburb to continue, or choose pick up.';
      } else {
        hint.textContent = '';
      }
    }

    if (dateInput) {
      dateInput.addEventListener('change', function () {
        if (!dateInput.value) {
          dateMsg.textContent = '';
          dateMsg.className = 'kh-wb__date-msg';
        } else if (isSunday(dateInput.value)) {
          dateMsg.textContent =
            'We are closed on Sundays, so we cannot deliver or hand over that day. Please choose another date.';
          dateMsg.className = 'kh-wb__date-msg kh-wb__date-msg--warn';
        } else if (dateInput.value < minDate) {
          dateMsg.textContent =
            'We need at least ' + leadDays + (leadDays === 1 ? ' day' : ' days') +
            ' to prepare your flowers. The earliest date we can take is ' + prettyDate(minDate) +
            '. For anything sooner, please call the studio.';
          dateMsg.className = 'kh-wb__date-msg kh-wb__date-msg--warn';
        } else {
          dateMsg.textContent = prettyDate(dateInput.value) + '. Noted on every item in your order.';
          dateMsg.className = 'kh-wb__date-msg kh-wb__date-msg--ok';
        }
        render();
      });
    }

    // Choosing a look only opens that range below. It must never put anything
    // into the order on its own - selecting a look used to drop a bridal
    // bouquet straight into the summary. (Al, 16 Aug 2026)
    lookInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        panels.forEach(function (panel) {
          panel.hidden = panel.dataset.khWbPanel !== input.value;
        });
        if (panelEmpty) panelEmpty.hidden = true;
        render();
      });
    });

    methodInputs.forEach(function (input) {
      input.addEventListener('change', render);
    });

    root.addEventListener('change', function (event) {
      if (event.target.closest && event.target.closest('[data-kh-wb-window]')) render();
    });

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-kh-wb-step]');
      if (!btn || !root.contains(btn)) return;
      var item = btn.closest('[data-kh-wb-item]');
      var qtyInput = item.querySelector('[data-kh-wb-qty]');
      var next = (parseInt(qtyInput.value, 10) || 0) + parseInt(btn.dataset.khWbStep, 10);
      if (next < 0) next = 0;
      if (next > 50) next = 50;
      qtyInput.value = next;
      item.classList.toggle('kh-wb__item--active', next > 0);
      syncDuplicates(item, next);
      render();
    });

    root.addEventListener('input', function (event) {
      var qtyInput = event.target.closest('[data-kh-wb-qty]');
      if (!qtyInput) return;
      var value = parseInt(qtyInput.value, 10);
      if (isNaN(value) || value < 0) value = 0;
      if (value > 50) value = 50;
      qtyInput.value = value;
      var item = qtyInput.closest('[data-kh-wb-item]');
      item.classList.toggle('kh-wb__item--active', value > 0);
      syncDuplicates(item, value);
      render();
    });

    submitBtn.addEventListener('click', function () {
      var items = selectedItems();
      var isDelivery = chosenMethod() === 'Delivery';
      if (!dateIsValid() || items.length === 0) return;
      if (isDelivery && !suburbHasRate()) return;

      var boxes = deliveryBoxes(items);
      var accOnly = isAccessoriesOnly(boxes);

      var properties = {};
      properties[DATE_PROPERTY] = prettyDate(dateInput.value);
      properties[METHOD_PROPERTY] = chosenMethod();
      if (isDelivery) {
        properties[SUBURB_PROPERTY] = suburb.name + ' ' + suburb.postcode;
        // No window promise on an accessories-only order: it travels as a
        // standard delivery (Kate, 12 Aug 2026).
        if (!accOnly) properties[WINDOW_PROPERTY] = chosenWindow();
      }

      var payload = {
        items: items.map(function (item) {
          return { id: item.id, quantity: item.quantity, properties: properties };
        })
      };

      submitBtn.disabled = true;
      submitBtn.classList.add('kh-wb__submit--busy');
      hint.classList.remove('kh-wb__hint--error');
      hint.textContent = 'Adding your flowers to the cart.';

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart responded ' + response.status);
          return response.json();
        })
        .then(function () {
          try {
            var estRate = accOnly ? standardRate() : weddingRate();
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'wedding_builder_add_to_cart',
              wedding_date: dateInput.value,
              fulfilment: chosenMethod(),
              delivery_suburb: isDelivery ? suburb.name + ' ' + suburb.postcode : null,
              delivery_window: isDelivery && !accOnly ? chosenWindow() : null,
              delivery_mode: isDelivery ? (accOnly ? 'standard' : 'wedding') : null,
              delivery_boxes: isDelivery ? boxes.total : 0,
              delivery_estimate: isDelivery ? estRate * boxes.total : 0,
              item_count: items.reduce(function (n, i) { return n + i.quantity; }, 0)
            });
          } catch (e) {
            /* analytics must never block the sale */
          }
          // The order lives in the real cart now, so the draft must not be
          // restored on the way back or it would be added twice.
          clearState();
          window.location.href = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart' : '/cart';
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.classList.remove('kh-wb__submit--busy');
          hint.classList.add('kh-wb__hint--error');
          hint.textContent = 'We could not add those to the cart. Please try again, or call the studio on 03 9510 8800.';
        });
    });

    root.khWbRender = render;
    restoreState();
    render();
  }

  function boot() {
    document.querySelectorAll('.kh-wb').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  // Coming back from a product page restores the inputs but not the summary,
  // so re-render on every show, bfcache included. (Al, 16 Aug 2026)
  window.addEventListener('pageshow', boot);
})();
